import React, { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    FlatList,
    StyleSheet,
    TouchableOpacity,
    Alert,
    RefreshControl,
    ActivityIndicator,
    SafeAreaView,
    Platform,
    Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getMySales } from '../../api/marketplace';
import { getShadow } from '../../theme/useTheme';

const VendorOrdersScreen = ({ navigation }) => {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'completed'

    const fetchSales = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const data = await getMySales();

            // Group by Order ID to show grouped orders if necessary, 
            // but the API returns OrderItems. Let's group them.
            const grouped = data.reduce((acc, item) => {
                const orderId = item.order_id;
                if (!acc[orderId]) {
                    acc[orderId] = {
                        ...item.order,
                        items: [],
                        total_sale_amount: 0
                    };
                }
                acc[orderId].items.push(item);
                acc[orderId].total_sale_amount += parseFloat(item.price_at_purchase) * item.quantity;
                return acc;
            }, {});

            setSales(Object.values(grouped).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (error) {
            console.error('Fetch Sales Error:', error);
            if (showLoading) {
                Alert.alert('Error', 'Unable to load orders');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchSales(false);
    };

    const activeStatuses = ['pending_payment', 'paid_processing', 'dispatched', 'delivered'];
    const completedStatuses = ['completed', 'cancelled'];

    const filteredOrders = sales.filter(order => {
        if (activeTab === 'active') {
            return activeStatuses.includes(order.status);
        } else {
            return completedStatuses.includes(order.status);
        }
    });

    const getStatusConfig = (status) => {
        switch (status) {
            case 'completed':
                return { label: 'COMPLETED', color: '#FFF', bg: '#10B981' };
            case 'delivered':
                return { label: 'DELIVERED', color: '#FFF', bg: '#059669' };
            case 'pending_payment':
                return { label: 'AWAITING PAYMENT', color: '#FFF', bg: '#F59E0B' };
            case 'paid_processing':
                return { label: 'READY TO SHIP', color: '#FFF', bg: '#6366F1' };
            case 'dispatched':
                return { label: 'IN TRANSIT', color: '#FFF', bg: '#3B82F6' };
            case 'cancelled':
                return { label: 'CANCELLED', color: '#FFF', bg: '#EF4444' };
            default:
                return { label: status?.toUpperCase()?.replace('_', ' ') || 'PENDING', color: '#FFF', bg: '#6B7280' };
        }
    };

    const renderOrder = ({ item }) => {
        const firstProduct = item.items[0]?.item;
        const itemCount = item.items.length;
        const status = getStatusConfig(item.status);
        const buyerName = item.buyer?.name || 'Anonymous';

        return (
            <TouchableOpacity
                style={styles.orderCard}
                onPress={() => navigation.navigate('VendorOrderDetail', { orderId: item.id })}
            >
                <View style={styles.cardHeader}>
                    <Text style={styles.orderNumber}>ORDER #{item.id}</Text>
                    <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                        <Text style={styles.statusText}>{status.label}</Text>
                    </View>
                </View>

                <View style={styles.cardBody}>
                    <Image
                        source={{ uri: firstProduct?.images?.[0] || 'https://via.placeholder.com/100' }}
                        style={styles.productImage}
                    />
                    <View style={styles.orderMainInfo}>
                        <Text style={styles.productTitle} numberOfLines={1}>
                            {firstProduct?.name || 'Item'} {itemCount > 1 ? `(+${itemCount - 1} more)` : ''}
                        </Text>
                        <View style={styles.buyerRow}>
                            <Ionicons name="person-circle-outline" size={14} color="#6B7280" />
                            <Text style={styles.buyerName}>{buyerName}</Text>
                        </View>
                        <Text style={styles.dateText}>{new Date(item.createdAt).toLocaleDateString()}</Text>
                    </View>
                    <View style={styles.amountContainer}>
                        <Text style={styles.amountText}>KES {item.total_sale_amount.toLocaleString()}</Text>
                        <Ionicons name="chevron-forward" size={18} color="#D1D5DB" />
                    </View>
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={24} color="#111" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Order Management</Text>
                <TouchableOpacity style={styles.searchButton}>
                    <Ionicons name="search" size={22} color="#111" />
                </TouchableOpacity>
            </View>

            <View style={styles.tabContainer}>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'active' && styles.activeTab]}
                    onPress={() => setActiveTab('active')}
                >
                    <Text style={[styles.tabText, activeTab === 'active' && styles.activeTabText]}>ACTIVE ORDERS</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'completed' && styles.activeTab]}
                    onPress={() => setActiveTab('completed')}
                >
                    <Text style={[styles.tabText, activeTab === 'completed' && styles.activeTabText]}>HISTORY</Text>
                </TouchableOpacity>
            </View>

            {loading && !refreshing ? (
                <View style={styles.centerContainer}>
                    <ActivityIndicator size="large" color="#6366F1" />
                </View>
            ) : (
                <FlatList
                    data={filteredOrders}
                    renderItem={renderOrder}
                    keyExtractor={item => item.id.toString()}
                    refreshControl={
                        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={['#6366F1']} />
                    }
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={
                        <View style={styles.emptyContainer}>
                            <Ionicons name="document-text-outline" size={64} color="#D1D5DB" />
                            <Text style={styles.emptyTitle}>No orders yet</Text>
                            <Text style={styles.emptySubtitle}>You don't have any {activeTab} orders at the moment.</Text>
                        </View>
                    }
                />
            )}
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#EEF2FF', // Clean Indigo background
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#FFF',
        paddingTop: Platform.OS === 'android' ? 35 : 10,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    backButton: {
        padding: 2,
    },
    headerTitle: {
        flex: 1,
        fontSize: 16,
        fontWeight: '800',
        color: '#111',
        textAlign: 'center',
    },
    searchButton: {
        padding: 2,
    },
    tabContainer: {
        flexDirection: 'row',
        backgroundColor: '#FFF',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    tab: {
        flex: 1,
        paddingVertical: 12,
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
    },
    activeTab: {
        borderBottomColor: '#6366F1',
    },
    tabText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#9CA3AF',
    },
    activeTabText: {
        color: '#6366F1',
    },
    listContent: {
        padding: 14,
    },
    orderCard: {
        backgroundColor: '#FFF',
        borderRadius: 14,
        padding: 12,
        marginBottom: 12,
        ...getShadow('xs'),
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#F8FAFC',
        paddingBottom: 8,
    },
    orderNumber: {
        fontSize: 11,
        fontWeight: '700',
        color: '#6B7280',
    },
    statusBadge: {
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 6,
    },
    statusText: {
        fontSize: 9,
        fontWeight: '800',
        color: '#FFF',
    },
    cardBody: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    productImage: {
        width: 48,
        height: 48,
        borderRadius: 10,
        backgroundColor: '#F3F4F6',
    },
    orderMainInfo: {
        flex: 1,
        marginLeft: 10,
    },
    productTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111',
        marginBottom: 2,
    },
    buyerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginBottom: 2,
    },
    buyerName: {
        fontSize: 12,
        color: '#4B5563',
    },
    dateText: {
        fontSize: 11,
        color: '#9CA3AF',
    },
    amountContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 2,
    },
    amountText: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111',
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#EEF2FF',
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#374151',
        marginTop: 12,
    },
    emptySubtitle: {
        fontSize: 13,
        color: '#9CA3AF',
        textAlign: 'center',
        marginTop: 6,
        paddingHorizontal: 30,
    },
});

export default VendorOrdersScreen;
